import { useState, useEffect, useCallback } from 'react';
import {
  getChildProfile,
  createChildProfile,
  updateChildProfile,
  dismissBirthYearPrompt,
} from '../services/childChaptersAPI';

/**
 * Hook for managing a child's profile data (age band, birth info, transition state).
 * @param {string} memberId - The family member (person) UUID
 */
export function useChildProfile(memberId) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getChildProfile(memberId);
      setProfile(data);
    } catch (err) {
      // 404 means no profile exists yet — that's expected
      if (err.response?.status === 404) {
        setProfile(null);
      } else {
        console.error('useChildProfile: Error fetching profile:', err);
        setError(err);
      }
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const create = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await createChildProfile(memberId, data);
      setProfile(result);
      return result;
    } catch (err) {
      console.error('useChildProfile: Error creating profile:', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  const update = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await updateChildProfile(memberId, data);
      setProfile(result);
      return result;
    } catch (err) {
      console.error('useChildProfile: Error updating profile:', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  const dismissBirthYear = useCallback(async () => {
    try {
      await dismissBirthYearPrompt(memberId);
      await fetchProfile();
    } catch (err) {
      console.error('useChildProfile: Error dismissing birth year prompt:', err);
    }
  }, [memberId, fetchProfile]);

  return {
    profile,
    loading,
    error,
    refetch: fetchProfile,
    create,
    update,
    dismissBirthYear,
  };
}
